import React, { useState } from "react";
import {
  ISviStatisticRow,
  ISviStatisticSummary,
} from "../../interfaces/services.interfaces";
import styles from "./StatisticTable.module.css";

const YEARS = [2021, 2022, 2023, 2024, 2025] as const;

type YearKey = (typeof YEARS)[number];

function getQuarterValue(
  row: ISviStatisticRow,
  year: YearKey,
  quarter: 1 | 2 | 3 | 4,
): string {
  const key = `q${year}_${quarter}` as keyof ISviStatisticRow;
  const val = row[key];
  if (typeof val === "string") return val;
  // Never substitute the yearly cell into quarter columns when a quarter field
  // is absent — that shows y2024 (e.g. E0) in Q1 even when Q1 should be empty.
  return "-";
}

function getCollapsedValue(row: ISviStatisticRow, year: YearKey): string {
  const q4 = getQuarterValue(row, year, 4);
  if (q4 !== "-") return q4;
  const yKey = `y${year}` as keyof ISviStatisticRow;
  return (row[yKey] as string) || "-";
}

export interface IStatisticTableProps {
  summary: ISviStatisticSummary;
  rows: ISviStatisticRow[];
  onRowClick?: (row: ISviStatisticRow) => void;
  onYearNavigate?: (year: number) => void;
  /** When true, body cells in expanded quarter columns use normal font weight (e.g. dream@supervision.earth). */
  normalWeightExpandedQuarterCells?: boolean;
}

const StatisticTable: React.FC<IStatisticTableProps> = ({
  summary,
  rows,
  onRowClick,
  normalWeightExpandedQuarterCells = false,
}) => {
  const [expandedYears, setExpandedYears] = useState<Record<number, boolean>>({
    2021: false,
    2022: false,
    2023: false,
    2024: false,
    2025: true,
  });

  const handleToggleYear = (year: number) => (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedYears((prev) => {
      const isCurrentlyExpanded = prev[year];
      if (isCurrentlyExpanded) {
        return { ...prev, [year]: false };
      }
      const allCollapsed = Object.fromEntries(
        YEARS.map((y) => [y, false]),
      ) as Record<number, boolean>;
      return { ...allCollapsed, [year]: true };
    });
  };

  const renderCell = (
    value: string,
    isNoProgressColumn = false,
    disableProgressColor = false,
  ) => {
    const trimmed = value.trim();
    const isEmptyDash = trimmed === "-" || trimmed === "";
    const isAlert =
      trimmed.toLowerCase().includes("alert") || trimmed.toUpperCase() === "E0";
    const isProgress = !disableProgressColor && !isEmptyDash && !isAlert;

    const classNames = [
      styles.td,
      isEmptyDash ? styles.cellMuted : "",
      isAlert ? styles.cellAlert : "",
      isAlert && isNoProgressColumn ? styles.cellAlertBackground : "",
      isProgress ? styles.cellProgress : "",
    ]
      .filter(Boolean)
      .join(" ");

    return <td className={classNames}>{trimmed}</td>;
  };

  const renderQuarterCell = (
    value: string,
    prevValue?: string,
    nextValue?: string,
  ) => {
    const trimmed = value.trim();
    const isEmptyDash = trimmed === "-" || trimmed === "";
    const isE0 = trimmed.toUpperCase() === "E0";
    const hasAlertInValue = trimmed.toLowerCase().includes("alert");
    const prevTrimmed = prevValue?.trim() ?? "";
    const nextTrimmed = nextValue?.trim() ?? "";
    const isValidVal = trimmed !== "" && trimmed !== "-";
    const sameAsPrev =
      isValidVal && prevTrimmed !== "" && prevTrimmed !== "-" && trimmed === prevTrimmed;
    const sameAsNext =
      isValidVal && nextTrimmed !== "" && nextTrimmed !== "-" && trimmed === nextTrimmed;
    const isStagnant = sameAsPrev || sameAsNext;
    const isAlert = isE0 || hasAlertInValue || isStagnant;
    const isProgress = false;

    const classNames = [
      styles.td,
      styles.quarterCell,
      normalWeightExpandedQuarterCells ? styles.quarterCellNormalWeight : "",
      isEmptyDash ? styles.cellMuted : "",
      isAlert ? styles.cellAlert : "",
      isProgress ? styles.cellProgress : "",
    ]
      .filter(Boolean)
      .join(" ");

    return <td className={classNames}>{trimmed}</td>;
  };

  const handleRowClick = (row: ISviStatisticRow) => {
    if (onRowClick) {
      onRowClick(row);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.summaryStrip}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>
            Average % of progress (last 3 months)
          </div>
          <div className={styles.summaryValueRow}>
            <span
              className={`${styles.summaryValue} ${styles.summaryGreen}`}
            >
              {summary.averageProgressPercent}
            </span>
            <span className={`${styles.summaryTrendIcon} ${styles.summaryGreen}`}>
              ▲
            </span>
          </div>
          <div className={styles.summarySubLabel}>Across all tracked objects</div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>
            Objects with visible progress (last 3 months)
          </div>
          <div className={styles.summaryValueRow}>
            <span
              className={`${styles.summaryValue} ${styles.summaryBrightGreen}`}
            >
              {summary.objectsWithVisibleProgress.toLocaleString()}
            </span>
            <span
              className={`${styles.summaryTrendIcon} ${styles.summaryBrightGreen}`}
            >
              ▲
            </span>
          </div>
          <div className={styles.summarySubLabel}>Showing measurable changes</div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Number of “No Progress” objects</div>
          <div className={styles.summaryValueRow}>
            <span className={`${styles.summaryValue} ${styles.summaryOrange}`}>
              {summary.noProgressObjects}
            </span>
            <span className={`${styles.summaryTrendIcon} ${styles.summaryOrange}`}>
              ▼
            </span>
          </div>
          <div className={styles.summarySubLabel}>Requires special attention</div>
        </div>

        <div className={styles.summaryCard}>
          <div className={styles.summaryLabel}>Most recent image capture</div>
          <div className={styles.summaryValueRow}>
            <span className={`${styles.summaryValue} ${styles.summaryBlue}`}>
              {summary.lastImageCaptureDate}
            </span>
          </div>
          <div className={styles.summarySubLabel}>Reference date for analysis</div>
        </div>
      </div>

      <div className={styles.tableWrapper}>
        <div className={styles.scrollContainer}>
          <table className={styles.table}>
            <colgroup>
              <col className={styles.colObjectId} />
              {YEARS.flatMap((year) =>
                expandedYears[year]
                  ? [1, 2, 3, 4].map((q) => (
                      <col
                        key={`${year}-q${q}`}
                        className={styles.colYear}
                      />
                    ))
                  : [<col key={year} className={styles.colYear} />],
              )}
              <col className={styles.colNoProgress} />
            </colgroup>
            <thead className={styles.thead}>
              <tr className={styles.headerTopRow}>
                <th className={styles.th}>Object ID</th>
                {YEARS.map((year) =>
                  expandedYears[year] ? (
                    <React.Fragment key={year}>
                      <th
                        className={`${styles.th} ${styles.quarterSubHeader}`}
                      >
                        {year} 1Q
                        <br />
                        <span className={styles.yearArrowPlaceholder} />
                      </th>
                      <th
                        className={`${styles.th} ${styles.quarterSubHeader}`}
                      >
                        {year} 2Q
                        <br />
                        <span className={styles.yearArrowPlaceholder} />
                      </th>
                      <th
                        className={`${styles.th} ${styles.quarterSubHeader}`}
                      >
                        {year} 3Q
                        <br />
                        <span className={styles.yearArrowPlaceholder} />
                      </th>
                      <th
                        className={`${styles.th} ${styles.quarterSubHeader}`}
                      >
                        {year} 4Q
                        <br />
                        <span
                          className={styles.yearArrow}
                          onClick={handleToggleYear(year)}
                        >
                          ←
                        </span>
                      </th>
                    </React.Fragment>
                  ) : (
                    <th key={year} className={styles.th}>
                      {year}
                      <br />
                      <span
                        className={styles.yearArrow}
                        onClick={handleToggleYear(year)}
                      >
                        ↔
                      </span>
                    </th>
                  ),
                )}
                <th className={`${styles.th} ${styles.noProgressHeader}`}>
                  No progress
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowClassNames = [
                  styles.tbodyRow,
                  onRowClick ? styles.clickableRow : "",
                  row.isPrimary ? styles.primaryRow : "",
                ]
                  .filter(Boolean)
                  .join(" ");

                return (
                  <tr
                    key={row.objectId}
                    className={rowClassNames}
                    onClick={() => handleRowClick(row)}
                  >
                    <td className={`${styles.td} ${styles.objectCell}`}>
                      <span className={styles.objectId}>
                        {row.objectId}
                      </span>
                      <span className={styles.objectName}>{row.name}</span>
                    </td>
                    {YEARS.map((year) => {
                      if (expandedYears[year]) {
                        const q1 = getQuarterValue(row, year, 1);
                        const q2 = getQuarterValue(row, year, 2);
                        const q3 = getQuarterValue(row, year, 3);
                        const q4 = getQuarterValue(row, year, 4);
                        return (
                          <React.Fragment key={year}>
                            {renderQuarterCell(q1, undefined, q2)}
                            {renderQuarterCell(q2, q1, q3)}
                            {renderQuarterCell(q3, q2, q4)}
                            {renderQuarterCell(q4, q3)}
                          </React.Fragment>
                        );
                      }
                      const collapsed = getCollapsedValue(row, year);
                      return (
                        <React.Fragment key={year}>
                          {year === 2025
                            ? renderCell(collapsed, false, false)
                            : renderCell(collapsed)}
                        </React.Fragment>
                      );
                    })}
                    {renderCell(row.noProgress, true)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default StatisticTable;

